import { BOT_URL, SAMPLE_POSTS } from "@/shared/content.js";
import { formatDate } from "@/shared/format.js";
import { uz } from "@/shared/i18n/uz.js";
import { ActionLink, Card, Container, PitchRule, SectionTitle } from "@/shared/ui/index.js";
import { usePageMeta } from "@/app/meta.js";
import { Link } from "@/app/router.js";
import { MatchCard } from "./MatchCard.js";

export function HomePage() {
  usePageMeta(null);

  return (
    <>
      <Hero />
      <Problems />
      <Steps />
      <Reliability />
      <PostsTeaser />
    </>
  );
}

function Hero() {
  return (
    <section className="bg-night text-night-ink">
      <Container className="grid gap-12 py-14 lg:grid-cols-[1.1fr_auto] lg:items-center lg:gap-16 lg:py-20">
        <div>
          <p className="text-gold text-sm font-medium">{uz.hero.eyebrow}</p>

          <h1 className="font-cond mt-3 text-5xl leading-[0.95] font-bold tracking-tight sm:text-6xl">
            {uz.hero.title}
          </h1>

          <p className="text-night-hint mt-5 max-w-[52ch] text-lg">{uz.hero.lead}</p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <ActionLink href={BOT_URL} external>
              {uz.hero.cta}
            </ActionLink>
            <ActionLink href="#qanday-ishlaydi" tone="outline">
              {uz.hero.secondary}
            </ActionLink>
          </div>

          <p className="text-night-hint mt-4 text-sm">{uz.hero.note}</p>
        </div>

        <div className="flex justify-center lg:justify-end">
          <MatchCard />
        </div>
      </Container>

      <PitchRule />
    </section>
  );
}

function Problems() {
  return (
    <Container className="py-16">
      <SectionTitle title={uz.problems.title} />

      <div className="grid gap-4 md:grid-cols-3">
        {uz.problems.items.map((item) => (
          <Card key={item.title} className="p-5">
            <h3 className="text-lg leading-snug font-semibold">{item.title}</h3>
            <p className="text-ink-2 mt-2">{item.text}</p>
          </Card>
        ))}
      </div>
    </Container>
  );
}

/**
 * Qadamlar raqamlangan — bu haqiqatan ketma-ketlik: kapitan avval o'yin
 * yaratadi, keyin ulashadi, oxirida jamoalarga bo'ladi.
 */
function Steps() {
  return (
    <Container className="py-4 pb-16">
      <div id="qanday-ishlaydi" className="scroll-mt-20">
        <SectionTitle title={uz.steps.title} />
      </div>

      <ol className="grid gap-8 md:grid-cols-3">
        {uz.steps.items.map((item, index) => (
          <li key={item.title} className="border-gold/50 border-t pt-4">
            <span className="font-cond text-gold block text-4xl leading-none font-bold">
              {index + 1}
            </span>
            <h3 className="mt-2 text-lg font-semibold">{item.title}</h3>
            <p className="text-ink-2 mt-1.5">{item.text}</p>
          </li>
        ))}
      </ol>
    </Container>
  );
}

function Reliability() {
  return (
    <section className="bg-night text-night-ink">
      <Container className="py-14">
        <div className="grid gap-8 lg:grid-cols-[1fr_1.1fr] lg:items-center lg:gap-16">
          <div>
            <SectionTitle title={uz.reliability.title} lead={uz.reliability.lead} onNight />
          </div>

          <dl className="divide-night-line border-night-line divide-y border-y">
            {uz.reliability.rows.map((row) => (
              <div key={row.label} className="flex items-baseline justify-between gap-6 py-4">
                <dt className="text-night-ink">{row.label}</dt>
                <dd
                  className={`font-cond tabular shrink-0 text-3xl leading-none font-bold ${
                    row.value.startsWith("−") ? "text-gold" : "text-green"
                  }`}
                >
                  {row.value}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </Container>
    </section>
  );
}

function PostsTeaser() {
  const latest = SAMPLE_POSTS.slice(0, 3);

  return (
    <Container className="py-16">
      <SectionTitle title={uz.posts.title} lead={uz.posts.lead} />

      <div className="grid gap-4 md:grid-cols-3">
        {latest.map((post) => (
          <Link
            key={post.slug}
            to={{ name: "post", slug: post.slug }}
            className="border-line bg-card hover:border-gold/60 flex flex-col rounded-xl border p-5 transition"
          >
            <span className="text-ink-3 flex items-center gap-2 text-xs">
              <span className="text-gold font-medium">{post.tag}</span>
              <span>{formatDate(post.date)}</span>
            </span>
            <h3 className="mt-2 text-lg leading-snug font-semibold">{post.title}</h3>
            <p className="text-ink-2 mt-2 text-sm">{post.excerpt}</p>
          </Link>
        ))}
      </div>
    </Container>
  );
}
